import { motion } from "framer-motion";
import { useState } from "react";
import { PageLayout } from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/SEO";
import {
  Users,
  TrendingUp,
  Gift,
  Eye,
  Link as LinkIcon,
  BarChart3,
  CreditCard,
  ArrowRight,
  Check,
  Sparkles,
  Target,
  Megaphone,
  GraduationCap,
  Newspaper,
  ShoppingCart,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const benefits = [
  { icon: Gift, text: "Comision preferențial față de programul public" },
  { icon: Eye, text: "Acces anticipat la platformă" },
  { icon: Megaphone, text: "Materiale promo exclusive înainte de lansare" },
  { icon: Users, text: "Posibilitatea de a fi listat ca Partener Fondator" },
  { icon: Sparkles, text: "Acces prioritar la noutăți și actualizări" },
];

const howItWorks = [
  { icon: LinkIcon, text: "Link unic de afiliere" },
  { icon: BarChart3, text: "Dashboard dedicat pentru tracking conversii" },
  { icon: TrendingUp, text: "Statistici clare privind comisioanele" },
  { icon: CreditCard, text: "Sistem de plată transparent" },
];

const audience = [
  { icon: Target, text: "Freelanceri SEO" },
  { icon: Megaphone, text: "Agenții de marketing digital" },
  { icon: Newspaper, text: "Creatori de conținut business / tech" },
  { icon: ShoppingCart, text: "Comunități eCommerce" },
  { icon: GraduationCap, text: "Traineri digital marketing" },
  { icon: Newspaper, text: "Proprietari de bloguri sau newslettere relevante" },
];

const faqItems = [
  {
    q: "Există costuri pentru a deveni afiliat?",
    a: "Nu. Aplicarea și participarea în program sunt gratuite.",
  },
  {
    q: "Când începe oficial programul?",
    a: "Programul complet va deveni activ odată cu lansarea versiunii finale, estimată la jumătatea anului 2026.",
  },
  {
    q: "Pot aplica dacă sunt la început?",
    a: "Da. Evaluăm fiecare aplicație în funcție de relevanță și calitatea canalului de promovare.",
  },
  {
    q: "Cum sunt plătite comisioanele?",
    a: "Detaliile privind structura de plată vor fi comunicate înainte de lansare oficială.",
  },
];

const ProgramAfiliere = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", website: "", message: "" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/affiliate-apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          alert('Ai aplicat deja pentru programul de afiliere!');
        } else {
          throw new Error(data.error || 'Failed to submit application');
        }
        return;
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Eroare la trimiterea aplicației. Te rog încearcă din nou sau contactează-ne la contactoptiro@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <SEO 
        title="Program Afiliat Fondator"
        description="Devino afiliat fondator OptiRO.ai și construiește venit recurent din noua eră SEO + GEO. Comisioane preferențiale și acces early."
        canonical="https://optiro.ai/program-afiliere"
      />
      {/* HERO */}
      <section className="py-20 md:py-28 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Badge className="mb-6" variant="secondary">
              Program Limitat
            </Badge>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground leading-tight mb-6">
              Devino <span className="text-gradient">Afiliat Fondator</span> OptiRO.ai
            </h1>
            <p className="text-lg text-muted-foreground mb-4 max-w-2xl mx-auto">
              Participă la lansarea primei platforme SEO + GEO din România și construiește venit recurent din viitorul optimizării pentru AI.
            </p>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              OptiRO.ai va fi lansat oficial la jumătatea anului 2026. Căutăm parteneri early care vor să promoveze noua generație de optimizare SEO + Generative Engine Optimization.
            </p>
            <Button size="lg" className="gap-2" onClick={() => document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" })}>
              Aplică pentru Programul Fondator
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Ce vei promova */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Ce vei promova</h2>
          <p className="text-muted-foreground mb-6">
            OptiRO.ai este o platformă care combină SEO clasic cu Generative Engine Optimization (GEO), ajutând site-urile să fie vizibile atât în Google, cât și în motoarele AI precum ChatGPT, Gemini și AI Overview.
          </p>
          <ul className="space-y-3 text-foreground">
            {[
              "Analiză SEO + GEO automată",
              "Evaluare a autorității și percepției AI",
              "Identificare oportunități de optimizare",
              "Monitorizare continuă",
              "Implementare optimizări controlate (după lansare)",
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3">
                <Check className="w-4 h-4 text-primary flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Beneficii */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Beneficiile Afiliatului Fondator</h2>
          <p className="text-muted-foreground mb-8">
            Programul de afiliere este în faza de pre-lansare. Afiliații fondatori vor beneficia de un comision superior față de afilierea standard de după lansare.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-background border border-border">
                <b.icon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-foreground">{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cum funcționează */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Cum funcționează afilierea OptiRO.ai</h2>
          <p className="text-muted-foreground mb-8">
            După lansarea oficială din 2026, fiecare afiliat va primi:
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {howItWorks.map((h, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-muted/50">
                <h.icon className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-sm text-foreground">{h.text}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground italic">
            În această etapă colectăm aplicații pentru lista afiliaților fondatori.
          </p>
        </div>
      </section>

      {/* Cui se adresează */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Cine poate aplica</h2>
          <p className="text-muted-foreground mb-8">Căutăm parteneri care activează în domenii precum:</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {audience.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg">
                <a.icon className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm text-foreground">{a.text}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-6 italic">Ne interesează calitate, nu volum.</p>
        </div>
      </section>

      {/* De ce acum */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">De ce este avantajul tău să intri acum</h2>
          <p className="text-muted-foreground mb-4">
            AI schimbă fundamental modul în care utilizatorii caută informații. Optimizarea pentru motoare generative este încă la început în România, iar brandurile care intră devreme vor avea avantaj competitiv semnificativ.
          </p>
          <p className="text-muted-foreground mb-4">
            Afiliații fondatori vor beneficia de condiții superioare înainte ca programul public să fie deschis.
          </p>
          <p className="font-medium text-foreground">
            Program limitat la un număr restrâns de parteneri fondatori.
          </p>
        </div>
      </section>

      {/* Formular */}
      <section id="apply-form" className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
            Aplică pentru Programul Afiliat Fondator
          </h2>

          {submitted ? (
            <Card className="text-center p-8">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">Aplicația a fost trimisă!</h3>
              <p className="text-muted-foreground">
                Vei fi contactat pe email dacă aplicația ta este aprobată pentru lista fondatorilor.
              </p>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="af-name">Nume complet</Label>
                    <Input id="af-name" placeholder="Ion Popescu" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="af-email">Email</Label>
                    <Input id="af-email" type="email" placeholder="ion@exemplu.ro" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="af-website">Website / Canal promovare</Label>
                    <Input id="af-website" placeholder="https://exemplu.ro" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="af-message">Cum ai promova OptiRO.ai?</Label>
                    <Textarea id="af-message" placeholder="Descrie pe scurt strategia ta..." className="min-h-[100px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? 'Se trimite...' : 'Trimite aplicația'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    Vei fi contactat pe email dacă aplicația ta este aprobată pentru lista fondatorilor.
                  </p>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 className="text-2xl font-bold text-foreground mb-6 text-center">Întrebări frecvente</h2>
          <Accordion type="single" collapsible>
            {faqItems.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            Construiește venit recurent din noua eră SEO + GEO.
          </h2>
          <Button size="lg" className="gap-2" onClick={() => document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" })}>
            Aplică acum pentru acces fondator
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>
    </PageLayout>
  );
};

export default ProgramAfiliere;
